import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import type {
    FileNode,
    FilePolicy,
    FileVisibility,
    SortDirection,
    SortField,
    StorageQuota,
    ViewMode,
} from './types';
import { 
    createFileNode, 
    permanentlyDeleteFile,
    subscribeToFiles
} from './utils/file-api';
import { cacheFile, removeCachedFile, getCachedFilesForProject } from './utils/offline-cache';
import { logger } from '@/utils/logger';
import { logBetaAnalyticsEvent, toFileSizeBucket, toMimeCategory } from '@/utils/beta-analytics';
import type { ProjectScope } from '../sync/project-scope';
import { getEffectiveUserId } from '../auth/current-user-identity';
import {
    createUploadSession,
    finalizeUpload,
    listFiles as listFilesViaService,
    listTrashedFiles,
    moveFileToTrash,
    requestFileAccess,
    restoreFileFromTrash,
    searchFiles as searchFilesViaService,
    updateFileMetadata,
    updateFilePolicy as updateFilePolicyViaService,
    uploadRepositoryFileVersion,
    uploadToSession,
} from './service';
import { accessSummaryForPolicy, normalizeFilePolicy, ownerOnlyPolicy } from './policy';

export interface FilesSlice {
    // State
    files: FileNode[];
    currentFolderId: string;
    selectedFileIds: string[];
    viewMode: ViewMode;
    sortField: SortField;
    sortDirection: SortDirection;
    searchQuery: string;
    isLoading: boolean;
    storageQuota: StorageQuota;
    showTrash: boolean;
    cachedFileIds: string[];

    // Actions
    filesActions: {
        // Navigation
        setCurrentFolder: (folderId: string) => void;
        navigateUp: () => Promise<void>;
        
        // File Operations
        loadFiles: (projectId: string, folderId?: string) => Promise<void>;
        uploadNewFile: (projectId: string, file: File, parentId: string, onProgress?: (percent: number) => void, policyOrVisibility?: FilePolicy | FileVisibility) => Promise<string>;
        createFolder: (projectId: string, name: string, parentId: string) => Promise<string>;
        renameFile: (projectId: string, fileId: string, newName: string) => Promise<void>;
        deleteFile: (projectId: string, fileId: string) => Promise<void>;
        updateFilePolicy: (projectId: string, fileId: string, policy: FilePolicy) => Promise<void>;
        
        // Versioning
        uploadNewVersion: (projectId: string, fileId: string, file: File) => Promise<void>;
        
        // Trash
        toggleTrash: () => void;
        loadTrash: (projectId: string) => Promise<void>;
        restoreFile: (projectId: string, fileId: string) => Promise<void>;
        permanentlyDeleteFile: (projectId: string, fileId: string) => Promise<void>;
        
        // Selection
        selectFile: (fileId: string) => void;
        deselectFile: (fileId: string) => void;
        clearSelection: () => void;
        selectAll: () => void;
        
        // View
        setViewMode: (mode: ViewMode) => void;
        setSortField: (field: SortField) => void;
        toggleSortDirection: () => void;
        
        // Search
        setSearchQuery: (query: string) => void;
        performSearch: (projectId: string) => Promise<void>;
        
        // Offline
        cacheFileForOffline: (file: FileNode) => Promise<void>;
        uncacheFile: (fileId: string) => Promise<void>;
        loadCachedFileIds: (projectId: string) => Promise<void>;
        
        // Quota
        updateStorageQuota: (quota: StorageQuota) => void;
        
        // Real-time sync
        subscribeToCurrentFolder: (projectId: string, folderId: string) => () => void;
        
        // Reset
        clearFiles: () => void;
    };
}

function getFileProjectScope(state: AppState & FilesSlice, projectId: string): ProjectScope | null {
    if (!state.cloud?.enabled || !state.cloud.orgId || state.cloud.projectId !== projectId) {
        return null;
    }
    return { orgId: state.cloud.orgId, projectId };
}

function upsertLocalFile(files: FileNode[], file: FileNode): void {
    const index = files.findIndex(existing => existing.id === file.id);
    if (index === -1) {
        files.push(file);
        return;
    }

    files[index] = file;
}

function isFilePolicyInput(value: FilePolicy | FileVisibility | undefined): value is FilePolicy {
    return typeof value === 'object' && value !== null;
}

function accessPolicyAnalyticsLabel(policy: FilePolicy): string {
    const summary = accessSummaryForPolicy(policy);
    if (summary === 'Anyone in project') {
        return 'project-wide';
    }
    if (summary === 'Restricted') {
        return 'restricted';
    }
    return 'owner-only';
}

export const createFilesSlice: StateCreator<
    AppState & FilesSlice,
    [["zustand/immer", never]],
    [],
    FilesSlice
> = (set, get) => ({
    files: [],
    currentFolderId: 'root',
    selectedFileIds: [],
    viewMode: 'grid',
    sortField: 'name',
    sortDirection: 'asc',
    searchQuery: '',
    isLoading: false,
    storageQuota: { usedBytes: 0, quotaBytes: 1024 * 1024 * 1024 }, // 1GB default
    showTrash: false,
    cachedFileIds: [],

    filesActions: {
        setCurrentFolder: (folderId) => set(state => {
            state.currentFolderId = folderId;
            state.selectedFileIds = [];
        }),

        navigateUp: () => {
            const { currentFolderId, files } = get();
            if (currentFolderId === 'root') return Promise.resolve();

            const currentFolder = files.find(f => f.id === currentFolderId);
            if (currentFolder) {
                set(state => {
                    state.currentFolderId = currentFolder.parentId;
                });
            }

            return Promise.resolve();
        },

        loadFiles: async (projectId, folderId = 'root') => {
            set(state => { state.isLoading = true; });
            try {
                const scope = getFileProjectScope(get(), projectId);
                if (!scope) throw new Error('Project organization scope is required to load files');
                const files = await listFilesViaService(scope, { parentId: folderId });
                set(state => {
                    state.files = files;
                    state.currentFolderId = folderId;
                    state.isLoading = false;
                });
            } catch (error: unknown) {
                logger.error('Failed to load files:', error);
                set(state => { state.isLoading = false; });
            }
        },

        uploadNewFile: async (projectId, file, parentId, onProgress, policyOrVisibility = 'all') => {
            const scope = getFileProjectScope(get(), projectId);
            if (!scope) throw new Error('Project organization scope is required to upload files');
            const ownerUserId = get().auth.user ? getEffectiveUserId() : undefined;
            if (!ownerUserId) throw new Error('Authentication required to upload files');

            const policy = isFilePolicyInput(policyOrVisibility)
                ? normalizeFilePolicy(policyOrVisibility, ownerUserId)
                : ownerOnlyPolicy(ownerUserId);
            if (policy.ownerUserId !== ownerUserId) {
                throw new Error('File policy owner must match authenticated user');
            }
            const session = await createUploadSession(scope, file);
            await uploadToSession(
                session,
                file,
                onProgress ? (progress) => onProgress(progress.percent) : undefined
            );
            const uploadedFile = await finalizeUpload(scope, {
                file,
                session,
                parentId,
                policy,
            });

            logBetaAnalyticsEvent('beta_file_uploaded', {
                mime_category: toMimeCategory(uploadedFile.mimeType ?? file.type),
                size_bucket: toFileSizeBucket(uploadedFile.size ?? file.size),
                access_policy: accessPolicyAnalyticsLabel(policy),
            });

            // Reload files
            await get().filesActions.loadFiles(projectId, parentId);
            set(state => {
                upsertLocalFile(state.files, uploadedFile);
            });

            return uploadedFile.id;
        },

        createFolder: async (projectId, name, parentId) => {
            const scope = getFileProjectScope(get(), projectId);
            if (!scope) throw new Error('Project organization scope is required to create folders');
            const folderId = await createFileNode(scope, parentId, name, 'folder');
            await get().filesActions.loadFiles(projectId, parentId);
            return folderId;
        },

        renameFile: async (projectId, fileId, newName) => {
            const scope = getFileProjectScope(get(), projectId);
            if (!scope) throw new Error('Project organization scope is required to rename files');
            await updateFileMetadata(scope, fileId, { name: newName });
            await get().filesActions.loadFiles(projectId, get().currentFolderId);
        },

        deleteFile: async (projectId, fileId) => {
            const scope = getFileProjectScope(get(), projectId);
            if (!scope) throw new Error('Project organization scope is required to delete files');
            await moveFileToTrash(scope, fileId);
            await get().filesActions.loadFiles(projectId, get().currentFolderId);
        },

        updateFilePolicy: async (projectId, fileId, policy) => {
            const scope = getFileProjectScope(get(), projectId);
            if (!scope) throw new Error('Project organization scope is required to update file access');
            await updateFilePolicyViaService(scope, fileId, policy);
            if (get().showTrash) {
                await get().filesActions.loadTrash(projectId);
            } else {
                await get().filesActions.loadFiles(projectId, get().currentFolderId);
            }
        },

        uploadNewVersion: async (projectId, fileId, file) => {
            const scope = getFileProjectScope(get(), projectId);
            if (!scope) throw new Error('Project organization scope is required to upload file versions');
            const existing = get().files.find(f => f.id === fileId);
            if (!existing) throw new Error('File not found');

            const newVersionNumber = (existing.versionNumber || 1) + 1;

            const result = await uploadRepositoryFileVersion(file, scope, fileId, newVersionNumber);

            await updateFileMetadata(scope, fileId, {
                versionNumber: newVersionNumber,
                previousVersionId: existing.id,
                storageRef: result.storagePath,
                storagePath: result.storagePath,
                size: result.size,
            });

            await get().filesActions.loadFiles(projectId, get().currentFolderId);
        },

        toggleTrash: () => set(state => { state.showTrash = !state.showTrash; }),

        loadTrash: async (projectId) => {
            set(state => { state.isLoading = true; });
            try {
                const scope = getFileProjectScope(get(), projectId);
                if (!scope) throw new Error('Project organization scope is required to load trash');
                const files = await listTrashedFiles(scope);
                set(state => {
                    state.files = files;
                    state.isLoading = false;
                    state.showTrash = true;
                });
            } catch (error: unknown) {
                logger.error('Failed to load trash:', error);
                set(state => { state.isLoading = false; });
            }
        },

        restoreFile: async (projectId, fileId) => {
            const scope = getFileProjectScope(get(), projectId);
            if (!scope) throw new Error('Project organization scope is required to restore files');
            await restoreFileFromTrash(scope, fileId);
            await get().filesActions.loadTrash(projectId);
        },

        permanentlyDeleteFile: async (projectId, fileId) => {
            const scope = getFileProjectScope(get(), projectId);
            if (!scope) throw new Error('Project organization scope is required to permanently delete files');
            await permanentlyDeleteFile(scope, fileId);
            await get().filesActions.loadTrash(projectId);
        },

        selectFile: (fileId) => set(state => {
            if (!state.selectedFileIds.includes(fileId)) {
                state.selectedFileIds.push(fileId);
            }
        }),

        deselectFile: (fileId) => set(state => {
            state.selectedFileIds = state.selectedFileIds.filter(id => id !== fileId);
        }),

        clearSelection: () => set(state => { state.selectedFileIds = []; }),

        selectAll: () => set(state => {
            state.selectedFileIds = state.files.map(f => f.id);
        }),

        setViewMode: (mode) => set(state => { state.viewMode = mode; }),

        setSortField: (field) => set(state => { state.sortField = field; }),

        toggleSortDirection: () => set(state => {
            state.sortDirection = state.sortDirection === 'asc' ? 'desc' : 'asc';
        }),

        setSearchQuery: (query) => set(state => { state.searchQuery = query; }),

        performSearch: async (projectId) => {
            const { searchQuery } = get();
            if (!searchQuery.trim()) {
                await get().filesActions.loadFiles(projectId, get().currentFolderId);
                return;
            }

            set(state => { state.isLoading = true; });
            try {
                const scope = getFileProjectScope(get(), projectId);
                if (!scope) throw new Error('Project organization scope is required to search files');
                const results = await searchFilesViaService(scope, searchQuery);
                set(state => {
                    state.files = results;
                    state.isLoading = false;
                });
            } catch (error: unknown) {
                logger.error('Search failed:', error);
                set(state => { state.isLoading = false; });
            }
        },

        cacheFileForOffline: async (file) => {
            const scope = getFileProjectScope(get(), file.projectId);
            if (!scope) throw new Error('Project organization scope is required to cache files');
            const access = await requestFileAccess(scope, file.id, 'download');
            await cacheFile(file.id, file.projectId, access.url, file.mimeType || '', file.name);
            set(state => {
                if (!state.cachedFileIds.includes(file.id)) {
                    state.cachedFileIds.push(file.id);
                }
            });
        },

        uncacheFile: async (fileId) => {
            await removeCachedFile(fileId);
            set(state => {
                state.cachedFileIds = state.cachedFileIds.filter(id => id !== fileId);
            });
        },

        loadCachedFileIds: async (projectId) => {
            const ids = await getCachedFilesForProject(projectId);
            set(state => { state.cachedFileIds = ids; });
        },

        updateStorageQuota: (quota) => set(state => { state.storageQuota = quota; }),

        subscribeToCurrentFolder: (projectId, folderId) => {
            const scope = getFileProjectScope(get(), projectId);
            if (!scope) return () => {};
            return subscribeToFiles(scope, folderId, (files) => {
                set(state => { state.files = files; });
            });
        },

        clearFiles: () => set(state => {
            state.files = [];
            state.currentFolderId = 'root';
            state.selectedFileIds = [];
            state.searchQuery = '';
            state.isLoading = false;
            state.showTrash = false;
            state.cachedFileIds = [];
        })
    }
});













